import { useState, useEffect, useRef, useId } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { pageURLs } from "@/constants";
import { getUserType } from "../../../../helpers/common";
import { fetchUserStatistics } from "../../../../actions/userprofile";
import { USER_INFO_KEY, USER_TYPE_AGENT } from "../../../../constants/common";
import PremiumPlanModal from "../../../../components/PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../../../components/PlanUpgrade/BasicPlanModal";
import { getLocalStorage } from "@/helpers/v2/common";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import { openModal } from "@/reducers/modals";
import { useAppConfig } from "@/appConfig";
import { isGitchaOwnerView, isMlsRestrictedTenant } from "@/helpers/tenant";
import { getAutocompletePredictions } from "@/services/placesService";
import { findExistingProperty } from "@/helpers/addressDedupe";
import { looksLikeStreetAddress } from "@/helpers/addressValidation";
import { useInventoryLocationRestrictions } from "@/hooks/useInventoryLocationRestrictions";
import QuickAddPropertyModal from "../QuickAddPropertyModal";
import { HousePlus } from "lucide-react";
import "../_match-cta-glow.scss";

const AddProperty = (props) => {
  const { userDetails, selectedClient } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userStatictics = useSelector(
    (state) => state?.userProfile?.userStatictics,
  );
  const userPrivileges = useSelector(
    (state) => state?.userProfile?.userPrivileges,
  );
  const [userCount, setUserCount] = useState({});
  const [allowedCount, setAllowedCount] = useState({});
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);
  const BasicPlanModalClose = () => setBasicPlanModal(false);
  const BasicPlanModalShow = () => setBasicPlanModal(true);
  const [reload, setReload] = useState(0);
  const userType = getUserType();

  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect();
  const { isRestrictedAddress, restrictionMessage } =
    useInventoryLocationRestrictions();
  const config = useAppConfig() || {};
  const { tenant } = config;
  const isBLS = isMlsRestrictedTenant(tenant);
  // Scope suggestions to the tenant's territory - a BLC agent typing "123 Main"
  // should not be offered California. locationRestriction/defaultMarket live
  // under tenant (see appConfig.js), not on the config object itself.
  const allowedStates = tenant?.locationRestriction?.enabled
    ? tenant.locationRestriction.states || []
    : [];
  const market = tenant?.defaultMarket;

  const lookupAddress = async (value) => {
    if (!value || value.trim().length < 4) {
      setAddressHints([]);
      return;
    }
    const opts = { types: ["address"], componentRestrictions: { country: ["us"] } };
    if (market?.latitude && market?.longitude && window?.google?.maps) {
      opts.location = new window.google.maps.LatLng(market.latitude, market.longitude);
      opts.radius = 80000; // ~50 miles around the tenant's market
    }
    const predictions = await getAutocompletePredictions(value, opts);
    const scoped = allowedStates.length
      ? predictions.filter((p) =>
          allowedStates.some((st) =>
            new RegExp(`,\\s*${st}(,|\\s|$)`, "i").test(p.description),
          ),
        )
      : predictions;
    const allowed = scoped.filter(
      (p) => !isRestrictedAddress(p.description),
    );
    setAddressHints(allowed.slice(0, 5));
  };

  // Places Autocomplete is billed per request - firing on every keystroke
  // multiplies request volume unnecessarily. Debounce so a pause in typing is
  // what actually triggers the lookup, matching the pattern in
  // MatchDemandPanel/index.js.
  const addressLookupDebounceRef = useRef(null);
  const lookupAddressDebounced = (value) => {
    if (addressLookupDebounceRef.current) {
      clearTimeout(addressLookupDebounceRef.current);
    }
    if (!value || value.trim().length < 4) {
      setAddressHints([]);
      return;
    }
    addressLookupDebounceRef.current = setTimeout(() => {
      lookupAddress(value);
    }, 300);
  };
  useEffect(() => {
    return () => {
      if (addressLookupDebounceRef.current) {
        clearTimeout(addressLookupDebounceRef.current);
      }
    };
  }, []);

  // The rail and the mobile drawer both mount this form, so a fixed id made
  // the drawer's label focus the hidden rail input.
  const inputId = useId();
  const inputRef = useRef(null);
  const [quickAddress, setQuickAddress] = useState("");
  const [addressHints, setAddressHints] = useState([]);
  const [addressError, setAddressError] = useState("");
  // Google's autocomplete is already restricted to types: ["address"], so a
  // picked suggestion is a real street address by construction - only a
  // hand-typed value needs the looksLikeStreetAddress gate. Reset on every
  // edit so a suggestion pick followed by more typing re-gates correctly.
  const [isFromSuggestion, setIsFromSuggestion] = useState(false);
  const [quickAddModal, setQuickAddModal] = useState({
    show: false,
    address: "",
  });
  const compact = props?.compact === true;
  const { existingProperties, onSelectExisting, onPropertyAdded } = props;

  // Typing an address they already own should select it, not create a
  // duplicate. See helpers/addressDedupe for the matching rules.
  const findExisting = (value) => findExistingProperty(value, existingProperties);
  // Not "is a member" - this panel also renders on the signed-out feed, where
  // getUserType() is null.
  const isOwnerView = isGitchaOwnerView(userType);
  const copy = isOwnerView
    ? {
        body: "Add your home and we'll alert you when a new want-listing matches it, instead of checking the feed every day.",
        cta: "Add my home",
      }
    : {
        body: isBLS
          ? "Add a home you're working on, like a prospect, a FSBO or an expired listing, and see which buyers want one like\u00a0it."
          : "Add your client's property to collect real-time demand insights and receive match alerts. The address is never published and nothing here is a listing.",
        cta: isBLS ? "Add a Prospect's Property" : "Add Inventory",
      };

  useEffect(() => {
    const payload = {
      UserPlanId: userDetails?.User_Plan_Id,
    };

    dispatch(fetchUserStatistics(payload));
  }, [userDetails]);

  useEffect(() => {
    if (userPrivileges !== null && userPrivileges.length > 0) {
      setAllowedCount(userPrivileges[0]);
    } else {
      setAllowedCount({});
    }
  }, [userPrivileges]);

  useEffect(() => {
    if (userStatictics !== null && userStatictics.length > 0) {
      //To fetch user statisctics
      setUserCount(userStatictics[0]);
    } else {
      setUserCount({});
    }
  }, [userStatictics]);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };
  /**
   * To handle add property
   */

  const user = getLocalStorage(USER_INFO_KEY);

  /**
   * The agent's fast path: confirm the property in a modal rather than sending
   * them into the full multi-step create flow for something an address lookup
   * can answer.
   *
   * Every gate the full route applies still applies here - signed out opens
   * login, a member keeps the original flow because their create asks for
   * things an address cannot answer, and an agent at their plan's inventory
   * limit still gets the upgrade modal rather than a quiet way around it.
   */
  const openQuickAdd = (addressValue) => {
    if (!user) {
      window.history.pushState({}, "", `${window.location.pathname}?auth=login`);
      dispatch(openModal({ name: "loginModal", data: { show: true } }));
      return;
    }
    if (userType !== USER_TYPE_AGENT) {
      handleAddProperty(addressValue);
      return;
    }
    if (userCount?.Inventory >= allowedCount?.Inventory) {
      PremiumPlanModalShow();
      return;
    }
    setQuickAddModal({ show: true, address: addressValue });
  };

  const handleAddProperty = (quickAddress = "") => {
    redirectToBLSEnv({
      path: pageURLs.agentCreateInventory, onGitchaEnvFallback: () => {

        if (!user) {
          window.history.pushState({}, "", `${window.location.pathname}?auth=login`);
          dispatch(openModal({ name: "loginModal", data: { show: true } }))
          return;
        }

        if (userCount?.Inventory >= allowedCount?.Inventory) {
          if (userType === USER_TYPE_AGENT) {
            PremiumPlanModalShow();
          } else {
            BasicPlanModalShow();
          }
        } else {
          if (userType === USER_TYPE_AGENT) {
            navigate(pageURLs.agentCreateInventory, {
              state: {
                data: {
                  clientId: selectedClient?.value,
                  clientName: selectedClient?.label,
                  isAgentAsSeller: selectedClient?.AgentAsSeller,
                  quickAddress: quickAddress || undefined,
                },
              },
            });
          } else {
            navigate(pageURLs.memberCreateInventory, {
              state: {
                data: {
                  quickAddress: quickAddress || undefined,
                },
              },
            });
          }
        }
      }
    })


  };

  /**
   * Shared by the plus button and by picking a suggestion. Picking a
   * suggestion used to only fill the field, leaving the agent to press plus to
   * confirm an address they had just chosen from a list - so a pick now
   * carries straight through.
   *
   * fromSuggestion is passed rather than read off state because setState is
   * asynchronous: the click that sets it also calls this, so reading state
   * here would still see the previous value and re-gate an address that came
   * from the list.
   */
  const submitAddress = (value, fromSuggestion) => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setAddressError("Enter the property's street address.");
      inputRef.current?.focus();
      return;
    }

    const already = findExisting(trimmed);
    if (already && onSelectExisting) {
      onSelectExisting(already);
      setQuickAddress("");
      setAddressError("");
      setAddressHints([]);
      return;
    }
    // A bare city/ZIP isn't a property Gitcha can match or report demand
    // on - require a real street address. Skipped for a picked
    // suggestion (already guaranteed by types: ["address"]) since the
    // regex is deliberately narrow and would otherwise dead-end valid
    // addresses it doesn't recognize (e.g. "One Microsoft Way").
    if (!fromSuggestion && !looksLikeStreetAddress(trimmed)) {
      setAddressError("Enter a full street address, not just a city or ZIP.");
      return;
    }
    // Suggestions are already filtered; this catches a hand-typed one.
    if (isRestrictedAddress(trimmed)) {
      setAddressError(restrictionMessage(trimmed));
      return;
    }
    setAddressError("");
    setAddressHints([]);
    openQuickAdd(trimmed);
  };

  const quickAddForm = (
    <form
      className="quick-add-property"
      onSubmit={(e) => {
        e.preventDefault();
        submitAddress(quickAddress, isFromSuggestion);
      }}
    >
      <label className="quick-add-property__label" htmlFor={inputId}>
        {copy.cta}
      </label>
      <div className="quick-add-property__row quick-add-property__row--inline">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          className="quick-add-property__input"
          placeholder="123 Main Street, City, State"
          value={quickAddress}
          onChange={(e) => {
            setQuickAddress(e.target.value);
            setAddressError("");
            setIsFromSuggestion(false);
            lookupAddressDebounced(e.target.value);
          }}
          autoComplete="off"
        />
        <button
          type="submit"
          className="quick-add-property__plus"
          aria-label={copy.cta}
          title={copy.cta}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 3.5v9M3.5 8h9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      {addressHints.length > 0 ? (
        <ul className="quick-add-property__hints">
          {addressHints.map((h) => (
            <li key={h.place_id}>
              <button
                type="button"
                onClick={() => {
                  setQuickAddress(h.description);
                  setAddressHints([]);
                  setAddressError("");
                  setIsFromSuggestion(true);
                  submitAddress(h.description, true);
                }}
              >
                {h.description}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {addressError ? (
        <p className="quick-add-property__error" role="alert">
          {addressError}
        </p>
      ) : (
        <p className="quick-add-property__hint">
          {isOwnerView
            ? "Your address is never published. It stays hidden, and only you can see it."
            : "Start typing a street address."}
        </p>
      )}
    </form>
  );

  return (
    <>
      {compact ? (
        quickAddForm
      ) : (
        <div
          className={`match-cta-glow${isOwnerView ? " match-cta-glow--quiet" : ""}`}
        >
          <div className="match-cta-glow__inner">
            {!isOwnerView ? (
              <span className="match-cta-glow__icon" aria-hidden="true">
                <HousePlus size={20} />
              </span>
            ) : null}
            <h3 className="match-cta-glow__heading">
              {isOwnerView
                ? (
                  <>
                    See which{" "}
                    <span className="match-cta-glow__nobreak">
                      want-listings
                    </span>{" "}
                    match your home
                  </>
                )
                : "See buyer demand for any\u00a0home"}
            </h3>
            <p className="match-cta-glow__body">{copy.body}</p>
            {quickAddForm}
          </div>
        </div>
      )}
      <PremiumPlanModal
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={PremiumPlanModalShow}
      />
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={BasicPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={BasicPlanModalShow}
      />
      <QuickAddPropertyModal
        show={quickAddModal.show}
        address={quickAddModal.address}
        selectedClient={selectedClient}
        onHide={() => setQuickAddModal({ show: false, address: "" })}
        onCreated={(property) => {
          setQuickAddModal({ show: false, address: "" });
          setQuickAddress("");
          setIsFromSuggestion(false);
          if (onPropertyAdded) onPropertyAdded(property);
        }}
      />
    </>
  );
};

export default AddProperty;
